import React, { useRef, useMemo, useCallback } from "react";
import { View, StyleSheet, TextInput } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import Badge from "@/components/ui/Badge";
import { EnumInput, EnumOption } from "@/components/ui/EnumInput";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import { PreviewIcon } from "./PreviewIcon";
import { CloseButton, ExpandCollapseButton, PinButton, SaveCancelButtons } from "./HeaderActions";
import type { StatusColumn } from "./PreviewHeader";

export interface CreateHeaderProps {
  title?: string;
  entityType?: string;

  // Single name input (for projects, etc.)
  name?: string;
  onNameChange?: (name: string) => void;
  namePlaceholder?: string;
  nameEditable?: boolean;

  // Description input (optional)
  description?: string;
  onDescriptionChange?: (description: string) => void;
  descriptionPlaceholder?: string;

  // First/last name inputs (for users, contacts, etc.)
  firstName?: string;
  lastName?: string;
  onFirstNameChange?: (name: string) => void;
  onLastNameChange?: (name: string) => void;
  useFirstLastName?: boolean;

  // Form state
  dirty?: boolean;
  saving?: boolean;

  // Actions
  onSave: () => void;
  onCancel: () => void;
  onClose?: () => void;

  /** General-purpose status columns rendered as labeled dropdowns with tooltips */
  statusColumns?: StatusColumn[];

  /** @deprecated Use statusColumns instead */
  status?: string;
  /** @deprecated Use statusColumns instead */
  onStatusChange?: (status: string) => void;
  /** @deprecated Use statusColumns instead */
  statusOptions?: EnumOption[];
  /** @deprecated Use statusColumns instead */
  secondaryStatus?: string;
  /** @deprecated Use statusColumns instead */
  onSecondaryStatusChange?: (status: string) => void;
  /** @deprecated Use statusColumns instead */
  secondaryStatusOptions?: EnumOption[];
  /** @deprecated Use statusColumns instead */
  secondaryStatusPlaceholder?: string;

  // Optional
  subtitle?: string;
  badges?: { text: string; tone?: "amber" | "emerald" | "indigo" }[];
  children?: React.ReactNode;
}

function StatusColumnInput({
  col,
  colors,
  toolTip,
}: {
  col: StatusColumn;
  colors: any;
  toolTip: ReturnType<typeof useToolTip>;
}) {
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = () => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>{col.label}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x: number, y: number, w: number) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + (w || 0) / 2 - 50, y: y - 38, width: 100, height: 30 } as any);
      toolTip.setVisible(true);
    });
  };

  const handlePointerLeave = () => {
    isHovering.current = false;
    toolTip.setVisible(false);
  };

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <EnumInput
        style={{ minWidth: 160 }}
        placeholder={col.placeholder || "Select status"}
        value={col.value}
        onSelect={col.onSelect || (() => {})}
        options={col.options}
        compact
      />
    </View>
  );
}

export function CreateHeader({
  title,
  entityType,
  name,
  onNameChange,
  namePlaceholder = "Name",
  nameEditable = true,
  description,
  onDescriptionChange,
  descriptionPlaceholder = "Description",
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  useFirstLastName = false,
  dirty = false,
  saving = false,
  onSave,
  onCancel,
  onClose,
  statusColumns: statusColumnsProp,
  status,
  onStatusChange,
  statusOptions,
  secondaryStatus,
  onSecondaryStatusChange,
  secondaryStatusOptions,
  secondaryStatusPlaceholder = "Select status",
  subtitle,
  badges,
  children,
}: CreateHeaderProps) {
  const { colors } = useTheme();
  const router = useRouter();

  const handleCancel = useCallback(() => {
    onCancel();
    router.setParams({ new: undefined, panel: undefined });
  }, [onCancel, router]);
  const toolTip = useToolTip();

  // Merge legacy props into statusColumns for backward compat
  const statusColumns = useMemo((): StatusColumn[] => {
    if (statusColumnsProp) return statusColumnsProp as StatusColumn[];
    const cols: StatusColumn[] = [];
    if (statusOptions && onStatusChange) {
      cols.push({
        label: "Status",
        key: "status",
        options: statusOptions,
        placeholder: "Select status",
        value: status,
        onSelect: onStatusChange,
      });
    }
    if (secondaryStatusOptions && onSecondaryStatusChange) {
      cols.push({
        label: secondaryStatusPlaceholder,
        key: "secondary_status",
        options: secondaryStatusOptions,
        placeholder: secondaryStatusPlaceholder,
        value: secondaryStatus,
        onSelect: onSecondaryStatusChange,
      });
    }
    return cols;
  }, [
    statusColumnsProp,
    statusOptions,
    onStatusChange,
    status,
    secondaryStatusOptions,
    onSecondaryStatusChange,
    secondaryStatus,
    secondaryStatusPlaceholder,
  ]);

  return (
    <View style={[styles.header, { backgroundColor: colors.card, borderBottomColor: colors.border }]}>
      <LinearGradient
        colors={[colors.primary, colors.secondary]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      />

      <View style={styles.headerContent}>
        {/* Icon - spans full height */}
        <PreviewIcon entityType={entityType} size={40} />

        {/* Content column: top row (title, status, buttons) + description row */}
        <View style={styles.contentColumn}>
          {/* Top row: title/name/badges, status, buttons */}
          <View style={styles.topRow}>
            <View style={styles.headerInfo}>
              {title && (
                <View style={{ gap: 8, flexDirection: "row", alignItems: "baseline" }}>
                  <ThemedText type="subtitle" style={{ opacity: 0.7 }}>
                    {title}
                  </ThemedText>
                </View>
              )}
              {useFirstLastName ? (
                <View style={styles.nameRow}>
                  <TextInput
                    style={[
                      styles.nameInput,
                      {
                        color: colors.text,
                        borderColor: colors.border,
                        backgroundColor: colors.backgroundSecondary,
                        flex: 1,
                      },
                    ]}
                    value={firstName}
                    onChangeText={onFirstNameChange}
                    placeholder="First name"
                    placeholderTextColor={colors.placeholder}
                  />
                  <TextInput
                    style={[
                      styles.nameInput,
                      {
                        color: colors.text,
                        borderColor: colors.border,
                        backgroundColor: colors.backgroundSecondary,
                        flex: 1,
                      },
                    ]}
                    value={lastName}
                    onChangeText={onLastNameChange}
                    placeholder="Last name"
                    placeholderTextColor={colors.placeholder}
                  />
                </View>
              ) : (
                <TextInput
                  style={[
                    styles.nameInput,
                    {
                      color: colors.text,
                      borderColor: colors.border,
                      backgroundColor: colors.backgroundSecondary,
                    },
                  ]}
                  value={name}
                  onChangeText={onNameChange}
                  placeholder={namePlaceholder}
                  placeholderTextColor={colors.placeholder}
                  editable={nameEditable}
                />
              )}
              {badges && badges.length > 0 && (
                <View style={styles.badges}>
                  {badges.map((badge, index) => (
                    <Badge key={index} text={badge.text} tone={badge.tone} />
                  ))}
                </View>
              )}
            </View>

            {statusColumns.map((col) => (
              <StatusColumnInput key={col.key} col={col} colors={colors} toolTip={toolTip} />
            ))}

            <View style={styles.headerActions}>
              <SaveCancelButtons onSave={onSave} onCancel={handleCancel} dirty={dirty} saving={saving} />
              <ExpandCollapseButton entityType={entityType} isCreateMode />
              <PinButton />
              {onClose && <CloseButton onClose={onClose} />}
            </View>
          </View>

          {/* Description row - underneath title/status/buttons */}
          {(onDescriptionChange || subtitle) && (
            <View style={styles.descriptionRow}>
              {onDescriptionChange ? (
                <TextInput
                  style={[
                    styles.descriptionInput,
                    {
                      color: colors.text,
                      borderColor: colors.border,
                      backgroundColor: colors.backgroundSecondary,
                    },
                  ]}
                  value={description}
                  onChangeText={onDescriptionChange}
                  placeholder={descriptionPlaceholder}
                  placeholderTextColor={colors.placeholder}
                />
              ) : (
                subtitle && <ThemedText style={{ opacity: 0.8 }}>{subtitle}</ThemedText>
              )}
            </View>
          )}
        </View>
      </View>

      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  headerGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "100%",
    opacity: 0.2,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 16,
    gap: 16,
  },
  contentColumn: {
    flex: 1,
    gap: 8,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  headerInfo: {
    gap: 4,
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  descriptionRow: {
    // Description spans full width under title/status/buttons
  },
  badges: {
    flexDirection: "row",
    gap: 6,
    marginTop: 6,
  },
  nameInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 18,
    fontWeight: "600",
    minWidth: 200,
  },
  descriptionInput: {
    height: 32,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 14,
    minWidth: 200,
  },
  nameRow: {
    flexDirection: "row",
    gap: 8,
  },
});
